<template>
    <section id="account-subscribed">
        <h3 class="text-uppercase mt-4">Subscription and billing</h3>
        <hr class="bar my-3" />

        <div>
            <p class="font-weight-bold mb-0">
                Expiration Date: {{ moment(subscription.renews_at).format("MM/DD/YYYY") }}
            </p>
            <p class="font-weight-bold mb-0">Subscription plan: {{ subscriptionTier }}</p>
            <p class="font-weight-bold mb-0">
                Renewal interval:
                <span class="text-capitalize">{{ subscription.interval + "ly" }}</span>
            </p>
            <p class="font-weight-bold">Seats: {{ subscription.quantity }}</p>
            <p>
                Thanks for subscribing to goLOTUS. Your subscription will automatically renew on
                {{ moment(subscription.renews_at).format("MM/DD/YYYY") }}. You can update your
                subscription and billing information below.
            </p>
            <p v-if="subscription.plan">
                Upcoming Renewal. Your subscription will automatically renew for ${{
                    subscription.amount_due
                }}
                plus sales tax where applicable each {{ subscription.interval }} unless you cancel
                your subscription.
            </p>
        </div>

        <div class="bg-white border-light p-3 mt-2">
            <h4 class="text-uppercase mb-3">Change your Subscription</h4>

            <div v-if="false && canChangePlan" class="my-2">
                <p class="mb-2 font-weight-bold">Subscription Type</p>
                <fieldset class="form-group">
                    <div class="row">
                        <div class="col-12">
                            <div class="form-check" v-for="p in plans">
                                <input
                                    class="form-check-input"
                                    type="radio"
                                    :id="p.id"
                                    v-model="plan"
                                    :value="p.id"
                                />
                                <label class="form-check-label" :for="p.id">
                                    {{ p.nickname }}
                                    <span
                                        >(${{ p.amount.toFixed(2) }} per seat per
                                        {{ p.interval }} plus sales tax where applicable)</span
                                    >
                                </label>
                            </div>
                        </div>
                    </div>
                </fieldset>
            </div>

            <coupon-widget
                :user="user"
                :subscription="subscription"
                @coupon-applied="applyCoupon($event)"
                @valid-coupon="changeCoupon($event)"
                @invalid-coupon="coupon = null"
            ></coupon-widget>

            <div>
                <template v-show="!editCard">
                    <div class="row">
                        <label class="col-sm-4 font-weight-bold text-capitalize"
                            >Credit card billed</label
                        >
                        <div class="col-sm-8">
                            <span v-if="subscription.card_last_four && subscription.card_brand"
                                ><span class="text-capitalize"
                                    >{{ user.cardholder_name ? `${user.cardholder_name} - ` : ""
                                    }}{{ subscription.card_brand }}</span
                                >
                                ending in {{ subscription.card_last_four }}</span
                            >
                            <button
                                @click.prevent="editCard = true"
                                class="small btn btn-link font-italic text-primary"
                            >
                                Update card
                            </button>
                        </div>
                    </div>
                </template>

                <div class="row" v-show="editCard">
                    <div class="col-12 col-8-sm my-2">
                        <label><b>Cardholder name</b></label>
                        <input
                            id="cardholder-name"
                            v-model="cardholderName"
                            class="form-control mb-3"
                            placeholder="Cardholder name"
                        />
                        <span class="mb-1"><b>Card Information</b></span>
                        <div id="stripe-card" class="form-control mt-2"></div>
                    </div>
                    <div class="col-12 mb-3 d-flex justify-content-between">
                        <button
                            @click.prevent="editCard = false"
                            class="btn btn-sm btn-link text-primary font-weight-bold text-uppercase"
                        >
                            Cancel update
                        </button>
                        <button
                            id="change-card"
                            @click.prevent="checkoutChangeCard()"
                            class="text-uppercase btn btn-sm btn-primary text-white"
                        >
                            Update card
                        </button>
                    </div>
                </div>
            </div>

            <div class="row">
                <label class="col-sm-4 font-weight-bold text-capitalize">Next billing date</label>
                <div class="col-sm-8" v-if="subscription.hasOwnProperty('renews_at')">
                    {{ moment(subscription.renews_at).format("MM/DD/YYYY") }}
                </div>
            </div>

            <!--PREVIEW CHANGES-->
            <div class="alert alert-warning mt-3" v-if="preview">
                By applying this change, you will be billed ${{ preview.amount_due.toFixed(2) }} on
                {{ preview.next_payment_attempt }}.
            </div>

            <div class="d-flex w-100 justify-content-end align-items-start mt-4 mb-2">
                <account-cancel
                    :user="user"
                    @subscription-canceled="$emit('subscription-changed')"
                ></account-cancel>

                <button
                    v-if="false && !editCard"
                    class="btn btn-primary font-weight-bold text-uppercase text-white"
                    id="change-plan"
                    @click="changePlan()"
                >
                    Update plan
                </button>
            </div>
        </div>
    </section>
</template>

<script>
import { helpers } from "../../plugins/helpers";
import AccountCancel from "./AccountCancel";
import CouponWidget from "../widgets/CouponWidget";

export default {
    name: "AccountSubscribed",
    props: ["user", "selectedPlan", "subscription", "plans", "preview", "isStripeMounted"],
    components: {
        AccountCancel,
        CouponWidget,
    },
    data() {
        return {
            coupon: null,
            plan: this.selectedPlan,
            editCard: false,
            cardholderName: "",
        };
    },
    mounted() {
        this.cardholderName = "";
        if (!this.isStripeMounted) {
            helpers.mountStripeElement("stripe-card");
            this.$emit("stripe-mounted");
        }
        this.plan = this.selectedPlan;
    },
    watch: {
        selectedPlan() {
            this.plan = this.selectedPlan;
        },

        plan(newVal, oldVal) {
            if (newVal !== oldVal && oldVal) {
                this.$emit("update-preview", { plan: this.plan, coupon: this.coupon });
            }
        },
    },

    computed: {
        canChangePlan() {
            if (this.subscription && this.subscription.plan) {
                if (this.subscription.plan.toLowerCase().includes("silver")) {
                    return true;
                }
            }
            return false;
        },

        subscriptionTier() {
            if (this.subscription.plan) {
                if (this.subscription.plan.toLowerCase().indexOf("platinum") === 0) {
                    return "Platinum";
                }
                if (this.subscription.plan.toLowerCase().indexOf("gold") === 0) {
                    return "Gold";
                }
                if (this.subscription.plan.toLowerCase().indexOf("silver") === 0) {
                    return "Gold (grandfathered)";
                }
            }
            return "";
        },

        selectedPlanPrice() {
            if (this.plan && this.plans.length) {
                let plan = this.plans.find(p => p.id === this.plan);
                return plan ? plan.amount.toFixed(2) : "";
            }
            return "";
        },

        selectedPlanInterval() {
            if (this.plan && this.plans.length) {
                let plan = this.plans.find(p => p.id === this.plan);
                return plan ? plan.interval : "";
            }
            return "";
        },
    },

    methods: {
        changeCoupon(coupon) {
            this.coupon = coupon;
            this.$emit("update-preview", { plan: this.plan, coupon: this.coupon });
        },

        applyCoupon(coupon) {
            this.$emit("subscription-changed");
        },

        checkoutChangeCard() {
            helpers.toggleSpinner("change-card");
            stripe
                .createPaymentMethod("card", card, {
                    billing_details: {
                        name: this.cardholderName,
                    },
                })
                .then(result => {
                    if (result.hasOwnProperty("error")) {
                        helpers.toggleSpinner("change-card");
                        return;
                    }
                    if (result.hasOwnProperty("paymentMethod")) {
                        this.changeCard(result);
                    }
                })
                .catch(error => {
                    console.error(error);
                    helpers.toggleSpinner("change-card");
                });
        },

        changeCard(result) {
            axios
                .post(`/api/users/${this.user.id}/subscription/update-card`, {
                    paymentMethod: result.paymentMethod,
                    cardholderName: this.cardholderName,
                })
                .then(r => {
                    helpers.toggleSpinner("change-card");
                    this.editCard = false;
                    this.$emit("subscription-changed");
                    this.$swal("Done!", `Your card was successfully updated.`, "success");
                })
                .catch(e => {
                    helpers.toggleSpinner("change-card");
                    console.error(e);
                    this.$swal("Error", `Something went wrong updating your card.`, "error");
                });
        },

        changePlan() {
            helpers.toggleSpinner("change-plan");
            axios
                .post(`/api/users/${this.user.id}/subscription/update-plan`, { plan: this.plan })
                .then(r => {
                    mixpanel.track(`Subscribe - Swap to ${this.plan}`);
                    helpers.toggleSpinner("change-plan");
                    this.$emit("subscription-changed");
                })
                .catch(e => {
                    helpers.toggleSpinner("change-plan");
                    console.error(e);
                });
        },
    },
};
</script>

<style scoped></style>
